<template>
  <div class="space-y-8">
    <!-- Header avec le bouton de souscription -->
    <div class="flex justify-between items-center">
      <h1 class="text-2xl font-semibold text-gray-900">Mon cheptel</h1>
      <router-link
        to="/subscribe"
        class="inline-flex items-center px-4 py-2 border border-zebu-orange rounded-lg text-sm font-medium text-zebu-orange hover:bg-zebu-orange hover:text-zebu-yellow transition-colors duration-300"
      >
        Souscrire à un projet
        <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
        </svg>
      </router-link>
    </div>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-zebu-orange"></div>
    </div>

    <div v-else-if="error" class="bg-red-50 p-4 rounded-lg">
      <p class="text-red-700">{{ error }}</p>
    </div>

    <div v-else class="space-y-8">
      <!-- Section "Vos plans" -->
      <div class="bg-white rounded-xl shadow-sm p-6">
        <h2 class="text-xl font-medium text-gray-900 mb-4">Vos plans</h2>
        <div v-if="projects?.length" class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div v-for="project in projects" :key="project.id" 
            class="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
            <div class="relative h-64">
              <img 
                :src="getProjectImage(project.Project.cover)"
                :alt="project.Project.name"
                @error="handleImageError"
                class="w-full h-full object-cover"
              />
            </div>
            <div class="p-6">
              <div class="space-y-4">
                <div class="flex justify-between items-start">
                  <div>
                    <router-link 
                      :to="{ name: 'PlanDetails', params: { id: project.id }}"
                      class="text-lg font-medium text-gray-900 hover:text-zebu-orange transition-colors"
                    >
                      {{ project.Project.name }}
                    </router-link>
                  </div>
                  <span :class="{
                    'px-2 py-1 text-xs font-medium rounded-full': true,
                    'bg-green-100 text-green-800': project.PlanStatus.label === 'Validé',
                    'bg-gray-100 text-gray-800': project.PlanStatus.label !== 'Validé'
                  }">
                    {{ project.PlanStatus.label }}
                  </span>
                </div>
                
                <div :class="[
                  'p-3 rounded-lg transition-colors duration-300',
                  project.plan_name 
                    ? 'bg-green-50 border border-green-100' 
                    : 'bg-amber-50 border border-amber-100'
                ]">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-sm font-medium" :class="[
                      project.plan_name ? 'text-green-800' : 'text-amber-800'
                    ]">Nom de votre animal</span>
                    <button
                      v-if="!project.isEditing"
                      @click="startEditing(project)"
                      class="text-sm hover:opacity-75 transition-opacity"
                      :class="[
                        project.plan_name ? 'text-green-600' : 'text-amber-600'
                      ]"
                    >
                      Modifier
                    </button>
                  </div>
                  <div v-if="project.isEditing" class="flex items-center space-x-2">
                    <input
                      type="text"
                      v-model="project.tempName"
                      placeholder="Donnez un nom à votre animal"
                      class="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm
                             focus:border-zebu-orange focus:ring-1 focus:ring-zebu-orange/20
                             hover:border-gray-300 transition-all duration-200
                             bg-white"
                      @keyup.enter="updatePlanName(project)"
                    />
                    <button
                      @click="updatePlanName(project)"
                      class="flex-shrink-0 p-2 text-zebu-orange hover:bg-white/50 rounded-lg transition-all duration-200"
                      title="Valider le nom"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                  <div v-else>
                    <div class="flex items-center space-x-2">
                      <p v-if="project.plan_name" class="text-gray-900 font-medium flex-grow">{{ project.plan_name }}</p>
                      <p v-else class="text-amber-600 italic flex-grow">Donnez un nom à votre animal !</p>
                      <svg v-if="project.plan_name" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div class="space-y-2">
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Projet:</span> 
                    {{ project.Project.name }}
                  </p>
                  <p v-if="project.peasant" class="text-sm text-gray-600">
                    <span class="font-medium">Paysan:</span> 
                    {{ project.peasant.firstname }} {{ project.peasant.lastname }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Souscrit le:</span> 
                    {{ new Date(project.subscribed_at).toLocaleDateString() }}
                  </p>
                  <p v-if="project.beneficiary?.name" class="text-sm text-gray-600">
                    <span class="font-medium">Bénéficiaire:</span> 
                    {{ project.beneficiary.name }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Montant:</span> 
                    {{ project.amount }}€
                  </p>
                  <p v-if="project.closed_at" class="text-sm text-gray-600">
                    <span class="font-medium">Clôturé le:</span> 
                    {{ new Date(project.closed_at).toLocaleDateString() }}
                  </p>
                  
                  <div v-if="project.isGift" class="mt-4 p-3 bg-zebu-yellow/10 rounded-lg">
                    <p class="text-sm text-gray-600">
                      <span class="font-medium">Reçu en cadeau</span>
                    </p>
                  </div>

                  <div class="mt-4">
                    <div class="flex space-x-3">
                      <router-link
                        :to="{ name: 'PlanDetails', params: { id: project.id }}"
                        class="inline-flex items-center px-4 py-2 border border-zebu-orange rounded-lg text-sm font-medium text-zebu-orange hover:bg-zebu-orange hover:text-zebu-yellow transition-colors duration-300"
                      >
                        Voir les détails
                        <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                        </svg>
                      </router-link>

                      <router-link
                        :to="{ name: 'CertificateView', params: { id: project.id }}"
                        class="inline-flex items-center px-4 py-2 border border-zebu-orange rounded-lg text-sm font-medium text-zebu-orange hover:bg-zebu-orange hover:text-zebu-yellow transition-colors duration-300"
                      >
                        Mon certificat
                        <svg class="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-center py-4">Vous n'avez pas encore de plans.</p>
      </div>

      <!-- Séparateur -->
      <hr class="border-gray-200" />

      <!-- Section "Vos cadeaux offerts/reçus" -->
      <div class="bg-white rounded-xl shadow-sm p-6">
        <h2 class="text-xl font-medium text-gray-900 mb-4">Vos cadeaux offerts/reçus</h2>
        <div v-if="giftedProjects?.length" class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div v-for="project in giftedProjects" :key="project.id" 
            class="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow">
            <div class="relative h-64">
              <img 
                :src="getProjectImage(project.Project.cover)"
                :alt="project.Project.name"
                @error="handleImageError"
                class="w-full h-full object-cover"
              />
            </div>
            <div class="p-6">
              <div class="space-y-4">
                <div class="flex justify-between items-start">
                  <div>
                    <h3 class="text-lg font-medium text-gray-900">{{ project.Project.name }}</h3>
                    <p class="text-sm text-gray-500">{{ project.PlanType.label }}</p>
                  </div>
                  <span :class="{
                    'px-2 py-1 text-xs font-medium rounded-full': true,
                    'bg-green-100 text-green-800': project.PlanStatus.label === 'Validé',
                    'bg-gray-100 text-gray-800': project.PlanStatus.label !== 'Validé'
                  }">
                    {{ project.PlanStatus.label }}
                  </span>
                </div>
                
                <div class="space-y-2">
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Plan:</span> 
                    {{ project.plan_name }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Projet:</span> 
                    {{ project.Project.name }}
                  </p>
                  <p v-if="project.peasant" class="text-sm text-gray-600">
                    <span class="font-medium">Paysan:</span> 
                    {{ project.peasant.firstname }} {{ project.peasant.lastname }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Souscrit le:</span> 
                    {{ new Date(project.subscribed_at).toLocaleDateString() }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Propriétaire:</span> 
                    {{ project.owner?.name }}
                  </p>
                  <p v-if="project.beneficiary?.name" class="text-sm text-gray-600">
                    <span class="font-medium">Bénéficiaire:</span> 
                    {{ project.beneficiary.name }}
                  </p>
                  <p class="text-sm text-gray-600">
                    <span class="font-medium">Montant:</span> 
                    {{ project.amount }}€
                  </p>
                  <p v-if="project.closed_at" class="text-sm text-gray-600">
                    <span class="font-medium">Clôturé le:</span> 
                    {{ new Date(project.closed_at).toLocaleDateString() }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-else class="text-gray-500 text-center py-4">Vous n'avez pas encore offert de cadeaux.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { memberareaService } from '@/services/memberarea.service';
import { useRouter } from 'vue-router';
import fallbackImage from '@/assets/fallback.jpg';
import { useToast } from 'vue-toastification'

const router = useRouter();
const loading = ref(true);
const error = ref(null);
const projectsData = ref(null);
const baseUrl = import.meta.env.VITE_API_URL;
const toast = useToast()

// Fonction pour gérer les trois sources d'images possibles
const getProjectImage = (cover) => {
  if (!cover) return fallbackImage
  
  // Nettoyer le chemin de l'image (enlever le premier slash si présent)
  const cleanPath = cover.startsWith('/') ? cover.slice(1) : cover
  const filename = cleanPath.split('/').pop()
  
  // On commence toujours par l'ancien système
  return `https://app.zebu.net/uploads/projects/${filename}`
}

// Gestionnaire d'erreur pour les images avec fallback en cascade
const handleImageError = (event) => {
  const img = event.target
  const currentSrc = img.src
  const filename = currentSrc.split('/').pop()
  
  // Ordre précis de fallback
  if (currentSrc.includes('app.zebu.net')) {
    // 1. Si l'ancien système échoue, on essaie le backend local
    console.log('Ancien système échoué, tentative backend local')
    img.src = `${baseUrl}/uploads/projects/${filename}`
  } else if (currentSrc.includes(baseUrl)) {
    // 2. Si le backend local échoue, on essaie le bucket
    console.log('Backend local échoué, tentative bucket')
    img.src = `https://bucket.zebu.net/images/${filename}`
  } else if (currentSrc.includes('bucket.zebu.net')) {
    // 3. Si le bucket échoue, on utilise le fallback
    console.log('Bucket échoué, utilisation du fallback')
    img.src = fallbackImage
  }
}

// Ajouter ces fonctions avant onMounted
const startEditing = (project) => {
  console.log('startEditing called for project:', project.id)
  // On force la réactivité en créant un nouvel objet
  const index = projects.value.findIndex(p => p.id === project.id)
  if (index !== -1) {
    projects.value[index] = {
      ...project,
      isEditing: true,
      tempName: project.plan_name || ''
    }
  }
}

const updatePlanName = async (project) => {
  try {
    console.log('updatePlanName called for project:', project.id)
    if (project.tempName && project.tempName.length > 50) {
      toast.warning('Le nom ne peut pas dépasser 50 caractères')
      return
    }

    const response = await memberareaService.updateHerdPlanName(project.id, project.tempName)
    if (response.success) {
      // On force la réactivité en créant un nouvel objet
      const index = projects.value.findIndex(p => p.id === project.id)
      if (index !== -1) {
        projects.value[index] = {
          ...project,
          plan_name: project.tempName,
          isEditing: false
        }
      }
      toast.success('Nom de votre animal mis à jour avec succès !')
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour du nom:', error)
    toast.error('Erreur lors de la mise à jour du nom')
    project.tempName = project.plan_name
  }
}

// Modifier le computed projects pour le rendre réactif
const projects = ref([]) // Changer en ref au lieu de computed

// Ajouter une fonction de tri par date
const sortByDate = (a, b) => {
  return new Date(b.subscribed_at) - new Date(a.subscribed_at)
}

// Modifier le onMounted pour trier les plans
onMounted(async () => {
  try {
    loading.value = true
    const response = await memberareaService.getHerdData()
    console.log('API Response:', response)
    if (response && response.success && response.data) {
      // Créer les objets et trier par date
      projects.value = response.data.yourPlans
        .map(plan => ({
          id: plan.id,
          Project: {
            name: plan.projectName,
            cover: plan.project_image
          },
          PlanType: {
            label: plan.planType
          },
          PlanStatus: {
            label: plan.status
          },
          plan_name: plan.name,
          amount: plan.amount,
          peasant: plan.peasant,
          owner: plan.owner,
          beneficiary: plan.beneficiary,
          subscribed_at: plan.subscribed_at,
          closed_at: plan.closed_at,
          isGift: plan.isGift,
          isEditing: false,
          tempName: plan.name || ''
        }))
        .sort(sortByDate)
    } else {
      error.value = "Format de données incorrect"
      console.error('Format de données incorrect:', response)
    }
  } catch (err) {
    error.value = "Impossible de charger les données du cheptel"
    console.error('Erreur lors du chargement du cheptel:', err)
  } finally {
    loading.value = false
  }
})

const giftedProjects = computed(() => {
  if (!projectsData.value?.data?.offeredGifts) return [];
  return projectsData.value.data.offeredGifts
    .map(plan => ({
      id: plan.id,
      Project: {
        name: plan.projectName,
        cover: plan.project_image
      },
      PlanType: {
        label: plan.planType
      },
      PlanStatus: {
        label: plan.status
      },
      plan_name: plan.name,
      amount: plan.amount,
      peasant: plan.peasant,
      owner: plan.owner,
      beneficiary: plan.beneficiary,
      subscribed_at: plan.subscribed_at,
      closed_at: plan.closed_at
    }))
    .sort(sortByDate);
});
</script>

<style scoped>
/* Mettre à jour le style pour le fallback */
img[src$="fallback.jpg"] {
  opacity: 0.5;
  background-color: #f3f4f6;
}
</style> 